import React, { useEffect } from 'react';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import StepContainer from '../StepContainer';
import TextInput from '../TextInput/TextInput';
import DateInput from '../DateInput/DateInput';
import FixedFooter from '../Footer/FixedFooter';
import Infographic from '../Infographic';

import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';

import { useHideIntercom } from '../../customHooks';

import styles from './ExtraDetails.module.scss';

import birthdayInfographic from '../../public/images/birthdate-infographic.svg';
import zipInfographic from '../../public/images/zip-infographic.svg';

const { saveLastScreen, updateUserPackageInfo, resetPlanPackageUpdated } = authUserOperations;

const {
  'birthdate-and-zip': birthdateAndZip,
  'custom-date': customDateClass,
  'zip-container': zipContainer,
  'date-container': dateContainer,
  'custom-infographic-img': customInfographicClass
} = styles;

const ExtraDetails = () => {
  const { authUser } = useSelector(({ authUser }) => ({
    authUser
  }));
  const { data: authUserData, planPackageUpdated, isFetching } = authUser ?? {};
  const dispatch = useDispatch();

  const estimateFormSchema = Yup.object().shape({
    birthMonth: Yup.number()
      .typeError('Enter valid number')
      .min(1, 'Enter valid month')
      .max(12, 'Enter valid month')
      .required('Month is required!'),
    birthDay: Yup.number()
      .typeError('Enter valid number')
      .min(1, 'Enter a valid day')
      .max(31, 'Enter a valid day')
      .required('Day is required!'),
    birthYear: Yup.number()
      .typeError('Enter valid number')
      .min(1880, 'Enter a valid year')
      .max(new Date().getFullYear(), 'Enter a valid year')
      .required('Year is required!'),
    zipcode: Yup.string()
      .required()
      .matches(/^[0-9]+$/, 'Enter a valid zipcode')
      .min(5, 'Enter a valid zipcode')
      .max(5, 'Enter a valid zipcode')
  });

  // Hide Intercom on mobile
  useHideIntercom(768);

  //update last screen after user update success
  useEffect(() => {
    if (planPackageUpdated) {
      dispatch(saveLastScreen({ screen: 'selectPackage' }));
      dispatch(resetPlanPackageUpdated());
    }
  }, [planPackageUpdated]);

  const handleSubmit = userData => {
    const { zipcode, birthMonth, birthDay, birthYear } = userData;
    const ISOBirthdate = new Date(birthYear, birthMonth - 1, birthDay).toISOString();
    // /v2/private/preneed/edituser will wipe null fields on user.userInfo not spread
    const userInfo = {
      ...authUserData.preneed.userInfo,
      dateOfBirth: ISOBirthdate,
      address: {
        zip: zipcode
      }
    };
    dispatch(updateUserPackageInfo({ userInfo }));
  };

  const birthDateInputProps = {
    label: 'Birthday',
    dayName: 'birthDay',
    dayPlaceholder: 'DD',
    monthName: 'birthMonth',
    monthPlaceholder: 'MM',
    yearName: 'birthYear',
    yearPlaceholder: 'YYYY'
  };

  return (
    <Formik
      validationSchema={estimateFormSchema}
      initialValues={{
        zipcode: '',
        birthMonth: '',
        birthDay: '',
        birthYear: ''
      }}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {({ values, isValid, handleSubmit }) => (
        <>
          <StepContainer
            evelynArray={[
              'Hi! I’m Evelyn. At Everdays, we’re passionate about helping people make funeral plans so their loved ones have the guidance and answers they’ll need when the time comes.',
              { content: 'Let’s start with key details that shape your plan.', style: 'header' }
            ]}
            hideNav={true}
            showLoader={isFetching}
          >
            <Form>
              <div className={birthdateAndZip}>
                <div className={dateContainer}>
                  <DateInput {...birthDateInputProps} customClass={customDateClass} />
                  <Infographic
                    imgUrl={birthdayInfographic}
                    infoCopy="Your age drives many of your pricing options."
                  />
                </div>
                <div className={zipContainer}>
                  <TextInput name="zipcode" label="Zip Code" placeholder="" />
                  <Infographic
                    imgUrl={zipInfographic}
                    infoCopy="Your location helps us find the best funeral homes in your area"
                  />
                </div>
              </div>
            </Form>
          </StepContainer>
          <FixedFooter {...{ isValid }} nextCopy="Next" nextAction={handleSubmit} />
        </>
      )}
    </Formik>
  );
};

export default ExtraDetails;
